import clsx from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, CircleAlert, Hammer, Info, ShieldQuestion, Undo2, X } from "lucide-react";
import { createContext, useContext, useState } from "react";
import { Button, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { spaceApi } from "@/lib/spaces";
import type { Part } from "@/lib/types";
import { AttachmentView } from "./Attachments";

type ToolPart = Extract<Part, { type: "tool_call" }>;

function prettyArgs(raw?: string): string {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

interface ProposedTask {
  name?: string;
  instructions?: string;
  text?: string;
  action?: string;
  condition_sql?: string;
  condition_when?: string;
  schedule?: { type?: string; at?: string; cron?: string; minutes?: number };
}

/** The space a chat is in, so changes its model made there can be undone from the chat. */
export const ChatSpace = createContext<string | null>(null);

const ACTIONS: Record<string, string> = { notify: "Sends a notification", checkin: "Starts a check-in chat that asks", agent: "The model carries out" };

const WEEKDAYS = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

/** A schedule in words, for the common cases; the cron expression otherwise. */
export function scheduleText(s: ProposedTask["schedule"]): string {
  if (!s) return "?";
  if (s.type === "once" && s.at) {
    const d = new Date(s.at);
    return isNaN(d.getTime()) ? `once at ${s.at}` : `once, ${d.toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}`;
  }
  if (s.type === "interval" && s.minutes) {
    const m = s.minutes;
    if (m % 1440 === 0) return m === 1440 ? "every day" : `every ${m / 1440} days`;
    if (m % 60 === 0) return m === 60 ? "every hour" : `every ${m / 60} hours`;
    return `every ${m} minutes`;
  }
  if (s.type === "cron" && s.cron) {
    const f = s.cron.trim().split(/\s+/);
    const time = /^\d+$/.test(f[0] ?? "") && /^\d+$/.test(f[1] ?? "") ? `${f[1].padStart(2, "0")}:${f[0].padStart(2, "0")}` : null;
    if (time && f.length === 5 && f[2] === "*" && f[3] === "*") {
      if (f[4] === "*") return `every day at ${time}`;
      if (/^[0-7]$/.test(f[4])) return `${WEEKDAYS[Number(f[4]) % 7]} at ${time}`;
      if (f[4] === "1-5") return `weekdays at ${time}`;
    }
    return `cron ${s.cron}`;
  }
  return "?";
}

/** What a tasks__create call proposes, readable for the approval decision. */
function TaskProposal({ raw, rule }: { raw?: string; rule?: boolean }) {
  let t: ProposedTask = {};
  try {
    t = JSON.parse(raw || "{}");
  } catch {
    return null;
  }
  return (
    <div className="space-y-1 border-t border-zinc-200 px-3 py-2 dark:border-zinc-800">
      <p className="text-sm">
        <span className="font-medium">{t.name || "Untitled task"}</span>
        <span className="text-zinc-500"> · {scheduleText(t.schedule)}</span>
      </p>
      {rule && t.condition_sql && (
        <p className="text-xs text-zinc-500">
          Only when this finds {t.condition_when === "none" ? "nothing" : "something"}: <code className="font-mono">{t.condition_sql}</code>
        </p>
      )}
      {(t.instructions || t.text) && (
        <p className="text-xs whitespace-pre-wrap text-zinc-600 dark:text-zinc-400">
          {rule && <span className="text-zinc-500">{ACTIONS[t.action ?? "agent"] ?? t.action}: </span>}
          {t.instructions || t.text}
        </p>
      )}
      <p className="text-xs text-zinc-500">
        {rule ? "A rule of this space. You can change or delete it under Tasks." : "Runs with this chat's model and tools; you get notified when it finds something. You can change or delete it under Tasks."}
      </p>
    </div>
  );
}

/** "Saved … (change abc)": what a space tool changed, with Undo. */
function SpaceChange({ part }: { part: ToolPart }) {
  const spaceId = useContext(ChatSpace);
  const qc = useQueryClient();
  const [state, setState] = useState<"idle" | "busy" | "undone" | string>("idle");
  const m = part.result_text?.match(/\(change ([0-9a-f]{12})[:)]/);
  if (!spaceId || !m || part.status !== "done") return null;
  const summary = part.result_text!.split("\n").filter((l) => !l.startsWith("(change ")).join(" ").replace(/\s*\(change [0-9a-f]{12}\)\.?$/, "").replace(/\.$/, "");
  async function undo() {
    setState("busy");
    try {
      await spaceApi.undo(spaceId!, m![1]);
      setState("undone");
      qc.invalidateQueries({ queryKey: ["space", spaceId] });
    } catch (e) {
      setState(e instanceof Error ? e.message : "Could not undo");
    }
  }
  return (
    <div className="flex items-start gap-2 border-t border-zinc-200 px-3 py-1.5 text-xs text-zinc-600 dark:border-zinc-800 dark:text-zinc-400">
      <span className={clsx("min-w-0 flex-1", state === "undone" && "line-through")}>{summary.length > 300 ? summary.slice(0, 300) + "…" : summary}</span>
      {state === "undone" ? (
        <span className="shrink-0 text-zinc-500">Undone</span>
      ) : (
        <button type="button" onClick={undo} disabled={state === "busy"} className="flex shrink-0 items-center gap-1 rounded px-1 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 dark:text-indigo-400 dark:hover:bg-indigo-950">
          <Undo2 size={12} /> Undo
        </button>
      )}
      {state !== "idle" && state !== "busy" && state !== "undone" && <span className="basis-full text-red-600 dark:text-red-400">{state}</span>}
    </div>
  );
}

function label(p: ToolPart): { server: string; tool: string } {
  const [server, ...rest] = p.name.split("__");
  return rest.length ? { server, tool: rest.join("__") } : { server: p.server ?? "", tool: p.name };
}

export function ToolCallCard({ part, messageId }: { part: ToolPart; messageId: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { server, tool } = label(part);
  const status = part.status ?? "pending";
  const waiting = status === "awaiting_approval";

  async function decide(approved: boolean) {
    setBusy(true);
    try {
      await api.post(`/api/messages/${messageId}/tool_calls/${part.id}/approve`, { approved });
    } finally {
      setBusy(false);
    }
  }

  const icon =
    status === "running" || status === "pending" ? (
      <Spinner size={13} />
    ) : status === "done" ? (
      <Check size={14} className="text-green-600" />
    ) : waiting ? (
      <ShieldQuestion size={14} className="text-amber-600" />
    ) : (
      <CircleAlert size={14} className="text-red-500" />
    );

  return (
    <div className="my-2">
      <div className={clsx("overflow-hidden rounded-lg border text-sm", waiting ? "border-amber-300 dark:border-amber-800" : "border-zinc-200 dark:border-zinc-800")}>
        <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-900">
          <Hammer size={14} className="shrink-0 text-zinc-400" />
          <span className="min-w-0 flex-1 truncate">
            {server && <span className="text-zinc-500">{server} · </span>}
            <span className="font-medium">{tool}</span>
            {status === "denied" && <span className="text-zinc-500"> (not allowed)</span>}
            {status === "running" && part.progress && <span className="text-xs text-zinc-500"> · {part.progress}</span>}
          </span>
          {icon}
          <ChevronDown size={14} className={clsx("shrink-0 text-zinc-400 transition-transform", open && "rotate-180")} />
        </button>
        {part.name === "tasks__create" && <TaskProposal raw={part.arguments} />}
        {part.name === "space__create_rule" && <TaskProposal raw={part.arguments} rule />}
        <SpaceChange part={part} />
        {waiting && (
          <div className="flex flex-wrap items-center gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/40">
            <span className="mr-auto text-xs text-amber-800 dark:text-amber-300">
              {part.name === "tasks__create"
                ? "Create this scheduled task?"
                : part.name === "space__create_rule"
                  ? "Create this rule?"
                  : part.name === "space__update_instructions"
                    ? "Replace the space's instructions? See Details."
                    : part.name === "space__change_schema"
                      ? "Change the database's tables? See Details."
                      : "This tool needs your approval to run."}
            </span>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => setOpen(true)}>
              Details
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => decide(false)}>
              <X size={14} /> Deny
            </Button>
            <Button size="sm" disabled={busy} onClick={() => decide(true)}>
              <Check size={14} /> Allow
            </Button>
          </div>
        )}
        {open && (
          <div className="space-y-2 border-t border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900/60">
            <div>
              <p className="mb-1 text-xs font-medium text-zinc-500">Arguments</p>
              <pre className="max-h-60 overflow-auto rounded bg-white p-2 font-mono text-xs dark:bg-zinc-950">{prettyArgs(part.arguments) || "{}"}</pre>
            </div>
            {part.result_text != null && (
              <div>
                <p className="mb-1 text-xs font-medium text-zinc-500">Result</p>
                <pre className="max-h-80 overflow-auto rounded bg-white p-2 font-mono text-xs whitespace-pre-wrap dark:bg-zinc-950">{part.result_text || "(no text)"}</pre>
              </div>
            )}
          </div>
        )}
      </div>
      {!!part.result?.length && (
        <div className="mt-2 flex flex-wrap">
          {part.result.map((r, i) => (r.type === "attachment" ? <AttachmentView key={i} id={r.id} name={r.name} mime={r.mime} /> : null))}
        </div>
      )}
    </div>
  );
}

export function Notice({ text }: { text: string }) {
  return (
    <p className="my-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
      <Info size={13} className="mt-0.5 shrink-0" />
      {text}
    </p>
  );
}
